'use client';

import { Button } from '@common/components';
import { SvgTextfieldclose } from '@common/icon';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  AuthQueryOptions,
  BANNER_CONTENTS_TYPE,
  UnauthQueryOptions,
} from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { SELECTOR } from '@/constants';
import { useCheckWelloApp } from '@/hooks/useCheckWelloApp';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';
import { useAuthStore } from '@/stores/AuthStore';

export const ImageFloatingBanner = () => {
  const [isClose, setIsClose] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const isLogin = useAuthStore((state) => state.isLogin);

  const { isWelloApp } = useCheckWelloApp();
  const { entry } = useWebviewTypeEntry();

  const { data } = useQuery(
    isLogin
      ? {
          ...AuthQueryOptions['get:/wello/v2/auth/banner']({
            query: {
              contentsTypeCd: BANNER_CONTENTS_TYPE.HOMETOWN_FLOATING,
            },
          }),
        }
      : {
          ...UnauthQueryOptions['get:/wello/v2/allows/banner']({
            query: {
              contentsTypeCd: BANNER_CONTENTS_TYPE.HOMETOWN_FLOATING,
              entry,
            },
          }),
        },
  );

  const banner = (data?.context?.contents ?? [])[0];

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [mainElement, setMainElement] = useState<HTMLElement>();

  useEffect(() => {
    const mainElement = document.getElementById(SELECTOR.MAIN);
    if (mainElement && isMounted) {
      setMainElement(mainElement);
    }
  }, [isMounted]);

  const applyFileUrl = isWelloApp ? banner?.appFileUrl : banner?.fileUrl;
  const applyLinkUrl = isWelloApp ? banner?.appLinkUrl : banner?.linkUrl;

  const isNoRender = isClose || !banner || !(applyFileUrl && applyLinkUrl);

  if (isNoRender) return null;

  return mainElement ? (
    createPortal(
      <CustomLink
        className="sticky bottom-70 z-[2] ml-auto mr-20 size-116 rounded-large"
        href={applyLinkUrl}
        target={banner.targetNewYN ? '_blank' : '_self'}
      >
        <section className="relative size-full">
          <Image
            alt={banner?.title ?? '프로모션 배너'}
            className="rounded-large object-cover"
            height={116}
            src={applyFileUrl}
            width={116}
          />
          <Button
            className="absolute -right-12 -top-12 size-fit p-0"
            theme="sub-text"
            onClick={(e) => {
              e.preventDefault();
              setIsClose(true);
            }}
          >
            <section className="relative flex size-24 items-center justify-center">
              <SvgTextfieldclose className="absolute left-0 top-0 size-full text-Gray-black opacity-100" />
              <div className="size-20 rounded-pill bg-Gray-white" />
            </section>
          </Button>
        </section>
      </CustomLink>,
      mainElement,
    )
  ) : (
    <></>
  );
};
